import { ApolloClient, ApolloLink } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities/internal';
import { OperationTypeNode } from 'graphql';
import { tap } from 'rxjs';

// Queries whose results can change after almost any mutation, so they refresh after every one.
const QUERIES_REFRESHED_AFTER_MUTATIONS = new Set(['PatientWorkQueues']);

// Mutations whose callers refetch these queries themselves, as soon as they finish.
const MUTATIONS_WITHOUT_REFETCH = new Set(['SnoozeWorkQueueItem', 'UnsnoozeWorkQueueItem']);

export const REFETCH_AFTER_MUTATION_DEBOUNCE_MS = 500;

const pendingRefetches = new WeakMap<ApolloClient, ReturnType<typeof setTimeout>>();

export const refetchQueriesRefreshedAfterMutations = (client: ApolloClient) =>
    client.refetchQueries({
        include: 'active',
        onQueryUpdated: (observableQuery) =>
            QUERIES_REFRESHED_AFTER_MUTATIONS.has(
                getOperationName(observableQuery.options.query, ''),
            ),
    });

export const scheduleRefetchAfterMutation = (client: ApolloClient) => {
    clearTimeout(pendingRefetches.get(client));
    pendingRefetches.set(
        client,
        setTimeout(() => {
            pendingRefetches.delete(client);
            void refetchQueriesRefreshedAfterMutations(client);
        }, REFETCH_AFTER_MUTATION_DEBOUNCE_MS),
    );
};

export const REFETCH_AFTER_MUTATION_LINK = new ApolloLink((operation, forward) => {
    if (
        operation.operationType !== OperationTypeNode.MUTATION ||
        MUTATIONS_WITHOUT_REFETCH.has(operation.operationName ?? '')
    ) {
        return forward(operation);
    }

    return forward(operation).pipe(
        tap((result) => {
            if (!result.errors?.length) {
                scheduleRefetchAfterMutation(operation.client);
            }
        }),
    );
});
